import dayjs from "dayjs";
import { z } from "zod";

import {
  createRequiredMessage,
  createSelectRequiredMessage,
  createMaxLengthMessage,
  HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
  DATE_RANGE_ERROR_MESSAGE,
  INVALID_DATE_FORMAT_MESSAGE,
  BEFORE_PURCHASE_DATE_MESSAGE,
  PRICE_LIMIT_MESSAGE,
  NON_NEGATIVE_MESSAGE,
  INTEGER_FORMAT_MESSAGE,
} from "../message";
import { HALF_WIDTH_NUMBER_REGEX, HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX, isWithinIntLimit, isNonNegative, isInteger } from "../patterns";

import { basicInfoSchema, priceLoanSchema, cashflowSchema, validateDateField } from "./common";
import { getPaymentPeriodError } from "./paymentPeriod";

// 物件情報タブ
// 物件情報編集スキーマ
export const editPropertyInfoBaseSchema = basicInfoSchema.omit({
  property_image: true, // 編集では画像アップロードは除外
});

// 物件情報編集スキーマ（区分物件用）
export const editPropertyInfoSchema = editPropertyInfoBaseSchema;

// 物件情報編集スキーマ（一棟物件用）
export const editPropertyInfoBuildingSchema = editPropertyInfoBaseSchema.extend({
  room_number: z.string().optional(),
  floor: z.string().optional(),
  occupied_area_m2: z.string().optional(),
  layout: z.string().optional(),
});

// 購入情報編集スキーマ
export const editPropertyPurchaseInfoSchema = priceLoanSchema
  .omit({
    // ローン関連は編集では除外
    loan_amount: true,
    loan_interest_rate_percentage: true,
    payment_starts_at: true,
    payment_period: true,
    // 編集では日付は文字列として扱うため日フィールドは不要
    bought_at_day: true,
  })
  .extend({
    priceof_facilities: z
      .string()
      .optional()
      .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
      .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
      .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE }),
    property_acquisition_tax: z
      .string()
      .optional()
      .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
      .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
      .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE }),
    property_acquisition_tax_payment_month: z.union([z.string(), z.date(), z.null()]).optional(),
    misc_expences_at_bought: z
      .string()
      .optional()
      .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
      .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
      .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE }),
  });

// 運用状況タブ
const baseInsuranceInfoHistorySchema = z.object({
  month: z
    .string()
    .min(1, createRequiredMessage())
    .refine(
      (val) => {
        const date = dayjs(val);
        return date.isValid();
      },
      {
        message: INVALID_DATE_FORMAT_MESSAGE,
      }
    ),
  // 火災保険用フィールド
  fire_insurance_cost: z
    .string()
    .min(1, createRequiredMessage())
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .optional(),
  fire_insurance_holding_period: z
    .string()
    .min(1, createRequiredMessage())
    .max(3, createMaxLengthMessage(3))
    .regex(HALF_WIDTH_NUMBER_REGEX, HALF_WIDTH_NUMBER_FORMAT_MESSAGE)
    .optional(),
  // 地震保険用フィールド
  earthquake_insurance_cost: z
    .string()
    .min(1, createRequiredMessage())
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .optional(),
  earthquake_insurance_holding_period: z
    .string()
    .min(1, createRequiredMessage())
    .max(3, createMaxLengthMessage(3))
    .regex(HALF_WIDTH_NUMBER_REGEX, HALF_WIDTH_NUMBER_FORMAT_MESSAGE)
    .optional(),
});

// 保険情報履歴編集スキーマ
export const createInsuranceInfoHistorySchema = (boughtAt: string) => {
  return baseInsuranceInfoHistorySchema.superRefine((data, ctx) => {
    // 購入月チェック
    const start = dayjs(data.month);
    const bought = dayjs(boughtAt).startOf("month");

    if (start.isBefore(bought)) {
      ctx.addIssue({
        code: "custom",
        path: ["month"],
        message: BEFORE_PURCHASE_DATE_MESSAGE,
      });
    }
  });
};

const baseLeaseContractHistorySchema = cashflowSchema.pick({
  starts_at: true,
  starts_at_day: true,
  ends_at: true,
  ends_at_day: true,
  monthly_fee: true,
  monthly_management_fee: true,
  other_monthly_income: true,
  type_of_the_income_of_the_beginning_of_contract: true,
  security_deposit: true,
  key_money: true,
  outsourcing_fee_rate_of_new_lease_contract: true,
  outsourcing_fee_rate_of_updating_lease_contract: true,
});

// リース契約履歴編集スキーマ
export const createLeaseContractHistorySchema = () => {
  return baseLeaseContractHistorySchema.superRefine((data, ctx) => {
    validateDateField(data.starts_at, data.starts_at_day, ["starts_at_day"], ctx);

    validateDateField(data.ends_at, data.ends_at_day, ["ends_at_day"], ctx);

    // 開始日 < 終了日チェック
    const startsAtDate = dayjs(data.starts_at);
    const endsAtDate = dayjs(data.ends_at);
    const startsAtDay = Number(data.starts_at_day);
    const endsAtDay = Number(data.ends_at_day);

    if (startsAtDate.isValid() && endsAtDate.isValid() && !isNaN(startsAtDay) && !isNaN(endsAtDay)) {
      const startDate = startsAtDate.date(startsAtDay);
      const endDate = endsAtDate.date(endsAtDay);

      if (startDate.isAfter(endDate) || startDate.isSame(endDate)) {
        ctx.addIssue({
          code: "custom",
          path: ["ends_at"],
          message: DATE_RANGE_ERROR_MESSAGE,
        });
      }
    }

    // security_deposit（更新料）
    if (data.type_of_the_income_of_the_beginning_of_contract === "SecurityDeposit") {
      if (!data.security_deposit || data.security_deposit.trim() === "") {
        ctx.addIssue({
          code: "custom",
          path: ["security_deposit"],
          message: createRequiredMessage(),
        });
      } else {
        // 半角数字（小数点含む）チェック
        if (!HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX.test(data.security_deposit)) {
          ctx.addIssue({
            code: "custom",
            path: ["security_deposit"],
            message: HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
          });
        }
      }
    }

    // key_money（礼金）
    if (data.type_of_the_income_of_the_beginning_of_contract === "KeyMoney") {
      if (!data.key_money || data.key_money.trim() === "") {
        ctx.addIssue({
          code: "custom",
          path: ["key_money"],
          message: createRequiredMessage(),
        });
      } else {
        // 半角数字（小数点含む）チェック
        if (!HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX.test(data.key_money)) {
          ctx.addIssue({
            code: "custom",
            path: ["key_money"],
            message: HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
          });
        }
      }
    }

    // outsourcing_fee_rate_of_new_lease_contract（新規賃貸契約手数料）
    if (data.type_of_the_income_of_the_beginning_of_contract === "KeyMoney") {
      if (
        !data.outsourcing_fee_rate_of_new_lease_contract ||
        data.outsourcing_fee_rate_of_new_lease_contract.trim() === ""
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["outsourcing_fee_rate_of_new_lease_contract"],
          message: createRequiredMessage(),
        });
      } else {
        // 半角数字（小数点含む）チェック
        if (!HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX.test(data.outsourcing_fee_rate_of_new_lease_contract)) {
          ctx.addIssue({
            code: "custom",
            path: ["outsourcing_fee_rate_of_new_lease_contract"],
            message: HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
          });
        }
      }
    }

    // outsourcing_fee_rate_of_updating_lease_contract（賃貸契約更新時手数料）
    if (data.type_of_the_income_of_the_beginning_of_contract === "SecurityDeposit") {
      if (
        !data.outsourcing_fee_rate_of_updating_lease_contract ||
        data.outsourcing_fee_rate_of_updating_lease_contract.trim() === ""
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["outsourcing_fee_rate_of_updating_lease_contract"],
          message: createRequiredMessage(),
        });
      } else {
        // 半角数字（小数点含む）チェック
        if (!HALF_WIDTH_NUMBER_WITH_DECIMAL_REGEX.test(data.outsourcing_fee_rate_of_updating_lease_contract)) {
          ctx.addIssue({
            code: "custom",
            path: ["outsourcing_fee_rate_of_updating_lease_contract"],
            message: HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
          });
        }
      }
    }
  });
};

const baseManagementCostHistorySchema = z.object({
  month: z
    .string()
    .min(1, createRequiredMessage())
    .refine(
      (val) => {
        const date = dayjs(val);
        return date.isValid();
      },
      {
        message: INVALID_DATE_FORMAT_MESSAGE,
      }
    ),
  building_management_cost: z
    .string()
    .min(1, createRequiredMessage())
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .optional(),
  management_cost: z
    .string()
    .min(1, createRequiredMessage())
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .optional(),
  repair_fund_cost: z
    .string()
    .min(1, createRequiredMessage())
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .optional(),
  communication_cost: z
    .string()
    .min(1, createRequiredMessage())
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .optional(),
  misc_cost: z
    .string()
    .min(1, createRequiredMessage())
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .optional(),
  property_tax: z
    .string()
    .min(1, createRequiredMessage())
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .optional(),
  supplies_cost: z
    .string()
    .min(1, createRequiredMessage())
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .optional(),
  large_scale_repair_fund_cost: z
    .string()
    .min(1, createRequiredMessage())
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .optional(),
  misc_onetime_cost: z
    .string()
    .min(1, createRequiredMessage())
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .optional(),
});

// 経費情報履歴編集スキーマ
export const createManagementCostHistorySchema = (boughtAt: string) => {
  return baseManagementCostHistorySchema.superRefine((data, ctx) => {
    // 購入月チェック
    const targetMonth = dayjs(data.month);
    const bought = dayjs(boughtAt).startOf("month");

    if (targetMonth.isBefore(bought)) {
      ctx.addIssue({
        code: "custom",
        path: ["month"],
        message: BEFORE_PURCHASE_DATE_MESSAGE,
      });
    }
  });
};

// ローン金額・返済期間編集スキーマ
export const loanAmountPeriodSchema = z.object({
  initial_loan_interest_rate_percentage: z.string().optional().refine(
    value => value == null || value === "" || (/^\d+(\.\d+)?$/.test(value) && Number(value) <= 100),
    "年利は0〜100%で入力してください。"
  ),
  payment_starts_at: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "初回返済年月を入力してください。"),
  loan_amount: z
    .string()
    .min(1, createRequiredMessage())
    .refine(isInteger, { message: INTEGER_FORMAT_MESSAGE })
    .refine(isWithinIntLimit, { message: PRICE_LIMIT_MESSAGE })
    .refine(isNonNegative, { message: NON_NEGATIVE_MESSAGE }),
  payment_period: priceLoanSchema.shape.payment_period,
}).superRefine((data, ctx) => {
  const message = getPaymentPeriodError(data.payment_period, data.loan_amount);
  if (message) ctx.addIssue({ code: "custom", path: ["payment_period"], message });
});

const baseLoanInfoHistorySchema = z.object({
  month: z
    .string()
    .min(1, createRequiredMessage())
    .refine(
      (val) => {
        const date = dayjs(val);
        return date.isValid();
      },
      {
        message: INVALID_DATE_FORMAT_MESSAGE,
      }
    ),
  loan_prepayment: z
    .string()
    .min(1, createRequiredMessage())
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .optional(),
  loan_prepayment_method: z.string().min(1, createSelectRequiredMessage()).optional(),
});

// ローン情報履歴編集スキーマ
export const createLoanInfoHistorySchema = (boughtAt?: string) => {
  return baseLoanInfoHistorySchema.superRefine((data, ctx) => {
    // 購入月チェック
    if (boughtAt) {
      const targetMonth = dayjs(data.month);
      const bought = dayjs(boughtAt).startOf("month");

      if (targetMonth.isBefore(bought)) {
        ctx.addIssue({
          code: "custom",
          path: ["month"],
          message: BEFORE_PURCHASE_DATE_MESSAGE,
        });
      }
    }
  });
};

// 建物管理費情報履歴編集スキーマ
const baseManagementBuildingCostHistorySchema = z.object({
  month: z
    .string()
    .min(1, createRequiredMessage())
    .refine(
      (val) => {
        const date = dayjs(val);
        return date.isValid();
      },
      {
        message: INVALID_DATE_FORMAT_MESSAGE,
      }
    ),
  property_tax: z
    .string()
    .min(1, createRequiredMessage())
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .optional(),
  supplies_cost: z
    .string()
    .min(1, createRequiredMessage())
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .optional(),
  large_scale_repair_fund_cost: z
    .string()
    .min(1, createRequiredMessage())
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .optional(),
  misc_onetime_cost: z
    .string()
    .min(1, createRequiredMessage())
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE })
    .optional(),
});

// 建物管理費情報履歴編集スキーマ
export const createManagementBuildingCostHistorySchema = (boughtAt: string) => {
  return baseManagementBuildingCostHistorySchema.superRefine((data, ctx) => {
    // 購入月チェック
    const targetMonth = dayjs(data.month);
    const bought = dayjs(boughtAt).startOf("month");

    if (targetMonth.isBefore(bought)) {
      ctx.addIssue({
        code: "custom",
        path: ["month"],
        message: BEFORE_PURCHASE_DATE_MESSAGE,
      });
    }
  });
};

// 部屋情報編集スキーマ
export const roomInfoSchema = z
  .object({
    room_number: z
      .string()
      .min(1, createRequiredMessage("部屋番号"))
      .regex(HALF_WIDTH_NUMBER_REGEX, HALF_WIDTH_NUMBER_FORMAT_MESSAGE),
    monthly_fee: z
      .union([z.string(), z.number()])
      .refine((val) => val !== "" && val !== 0, {
        message: createRequiredMessage("月間家賃収入"),
      })
      .refine((val) => isInteger(String(val)), { message: INTEGER_FORMAT_MESSAGE })
      .refine((val) => isWithinIntLimit(String(val)), { message: PRICE_LIMIT_MESSAGE })
      .refine((val) => isNonNegative(String(val)), { message: NON_NEGATIVE_MESSAGE }),
    other_monthly_income: z
      .union([z.string(), z.number()])
      .refine((val) => val !== "" && val !== 0, {
        message: createRequiredMessage("その他収入"),
      })
      .refine((val) => isInteger(String(val)), { message: INTEGER_FORMAT_MESSAGE })
      .refine((val) => isWithinIntLimit(String(val)), { message: PRICE_LIMIT_MESSAGE })
      .refine((val) => isNonNegative(String(val)), { message: NON_NEGATIVE_MESSAGE }),
    starts_at: z.date({
      required_error: createRequiredMessage("開始日"),
    }),
    starts_at_day: z
      .string()
      .min(1, createRequiredMessage())
      .regex(HALF_WIDTH_NUMBER_REGEX, HALF_WIDTH_NUMBER_FORMAT_MESSAGE),
    ends_at: z.date({
      required_error: createRequiredMessage("終了日"),
    }),
    ends_at_day: z
      .string()
      .min(1, createRequiredMessage())
      .regex(HALF_WIDTH_NUMBER_REGEX, HALF_WIDTH_NUMBER_FORMAT_MESSAGE),
  })
  .superRefine((data, ctx) => {
    validateDateField(data.starts_at, data.starts_at_day, ["starts_at_day"], ctx);
    validateDateField(data.ends_at, data.ends_at_day, ["ends_at_day"], ctx);

    // 開始日 < 終了日チェック
    if (data.starts_at && data.ends_at && data.starts_at_day && data.ends_at_day) {
      const startsAtDate = dayjs(data.starts_at);
      const endsAtDate = dayjs(data.ends_at);
      const startDate = startsAtDate.date(Number(data.starts_at_day));
      const endDate = endsAtDate.date(Number(data.ends_at_day));

      if (startDate.isAfter(endDate) || startDate.isSame(endDate)) {
        ctx.addIssue({
          code: "custom",
          path: ["ends_at"],
          message: DATE_RANGE_ERROR_MESSAGE,
        });
      }
    }
  });

// 型定義
export type EditPropertyInfoFormData = z.infer<typeof editPropertyInfoSchema>;
export type EditPropertyInfoBuildingFormData = z.infer<typeof editPropertyInfoBuildingSchema>;
export type EditPropertyPurchaseInfoFormData = z.infer<typeof editPropertyPurchaseInfoSchema>;
export type InsuranceInfoHistoryFormData = z.infer<typeof baseInsuranceInfoHistorySchema>;
export type LeaseContractHistoryFormData = z.infer<typeof baseLeaseContractHistorySchema>;
export type ManagementCostHistoryFormData = z.infer<typeof baseManagementCostHistorySchema>;
export type LoanInfoHistoryFormData = z.infer<typeof baseLoanInfoHistorySchema>;
export type LoanAmountPeriodFormData = z.infer<typeof loanAmountPeriodSchema>;
export type ManagementBuildingCostHistoryFormData = z.infer<typeof baseManagementBuildingCostHistorySchema>;
export type RoomInfoFormData = z.infer<typeof roomInfoSchema>;
